import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatCount,
  formatMb,
  formatRelativeTime,
} from '../../utils/dashboardFormatters';
import { buildFileManagerLink } from '../../utils/fileManagerQuery';
import './DashboardPulse.css';

const LOW_VOLUME_THRESHOLD = 10;
const ONBOARDING_THRESHOLD = 20;

const RANGES = [
  ['today', 'Hoy'],
  ['7d', '7 días'],
  ['30d', '30 días'],
  ['all', 'Histórico'],
];

const RANGE_LABELS = {
  today: 'hoy',
  '7d': 'últimos 7 días',
  '30d': 'últimos 30 días',
  all: 'histórico',
};

const STATUS_META = {
  ok: { label: 'Sistema operativo', dot: 'ok' },
  attention: { label: 'Requiere atención', dot: 'attention' },
  error: { label: 'Errores activos', dot: 'error' },
};

const SUBTYPE_COLORS = {
  INVOICE: '#0066cc',
  DEBITO: '#dc2626',
  CREDITO: '#059669',
  OTROS: '#6b7280',
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const normalizeRatio = (value) => {
  const number = toNumber(value);
  return number > 1 ? number / 100 : number;
};

const pct = (value) => `${(normalizeRatio(value) * 100).toFixed(0)}%`;

const trendFromValue = (value) => {
  const base = Math.max(toNumber(value), 0);
  if (!base) return [0, 0, 0, 0, 0, 0, 0];
  return [0.18, 0.34, 0.28, 0.52, 0.48, 0.72, 1].map((factor) =>
    Math.max(1, Math.round(base * factor))
  );
};

const buildRows = (items, { keyField, labelField, countField = 'count', pctField = 'percentage' }) =>
  (items || []).map((item) => {
    const key = item[keyField] || item[labelField] || item.currency || item.origin || item.subtype;
    const count = toNumber(item[countField]);
    return {
      key,
      label: item[labelField] || key || 'Sin dato',
      count,
      pct: normalizeRatio(item[pctField]),
      color: SUBTYPE_COLORS[key] || '#0066cc',
    };
  });

export function buildCommandCenterModel(stats, range = '7d') {
  const daily = stats?.daily_stats || {};
  const useDaily = range === 'today';
  const processed = useDaily ? toNumber(daily.processed_count) : toNumber(stats?.total_documents);
  const volumeMb = useDaily
    ? toNumber(daily.processed_size_mb)
    : toNumber(stats?.total_size) / 1024;
  const successRate = normalizeRatio(stats?.success_rate ?? 1);
  const openErrors = toNumber(stats?.errors);
  const inProcess = toNumber(stats?.pending);
  const unclassifiedCount = (stats?.recent_unclassified || []).length;
  const currencyStats = useDaily ? stats?.daily_currency_stats : stats?.currency_stats;
  const status = openErrors > 0 ? 'error' : successRate < 0.9 ? 'attention' : 'ok';
  const subtypeRows = buildRows(useDaily ? stats?.daily_invoice_subtype_stats : stats?.invoice_subtype_stats, {
    keyField: 'subtype',
    labelField: 'label',
  });
  const originRows = buildRows(useDaily ? stats?.daily_origin_stats : stats?.origin_stats, {
    keyField: 'origin',
    labelField: 'label',
  });
  const currencyRows = buildRows(currencyStats?.breakdown, {
    keyField: 'currency',
    labelField: 'currency',
  });
  const emailStats = stats?.email_stats || {};
  const fallbackStats = useDaily
    ? stats?.daily_extraction_fallback_stats
    : stats?.extraction_fallback_stats;
  const rangeLabel = RANGE_LABELS[range] || RANGE_LABELS['7d'];

  return {
    range,
    rangeLabel,
    health: {
      status,
      successRate,
      openErrors,
      inProcess,
      avgSeconds: toNumber(stats?.avg_duration),
    },
    hero: {
      processed,
      volumeMb,
      trend: trendFromValue(processed),
      deltaPct: 0,
    },
    attention: [
      {
        key: 'unclassified',
        label: 'sin clasificar',
        count: unclassifiedCount,
        severity: 'warning',
        href: buildFileManagerLink({ doc_type: 'unclassified' }),
      },
      {
        key: 'open_errors',
        label: 'errores abiertos',
        count: openErrors,
        severity: 'error',
        href: buildFileManagerLink({ status: 'PROCESS_FAILED' }),
      },
      {
        key: 'in_process',
        label: 'en proceso',
        count: inProcess,
        severity: 'info',
        href: buildFileManagerLink({ status: 'OCR_IN_PROGRESS' }),
      },
    ],
    kpis: [
      {
        key: 'file_volume',
        label: 'Volumen archivos',
        value: volumeMb,
        format: 'mb',
        trend: trendFromValue(volumeMb),
        deltaPct: 0,
      },
      {
        key: 'documents',
        label: 'Comprobantes',
        value: processed,
        format: 'integer',
        trend: trendFromValue(processed),
        deltaPct: 0,
      },
      {
        key: 'success_rate',
        label: 'Tasa de éxito',
        value: successRate,
        format: 'percent',
        trend: [successRate, successRate, successRate, successRate, successRate, successRate, successRate],
        deltaPct: 0,
      },
      {
        key: 'avg_time',
        label: 'Tiempo prom./archivo',
        value: toNumber(stats?.avg_duration),
        format: 'duration',
        trend: trendFromValue(stats?.avg_duration || 1),
        deltaPct: 0,
        lowerIsBetter: true,
      },
    ],
    composition: {
      lowVolume: processed < LOW_VOLUME_THRESHOLD,
      bySubtype: subtypeRows,
      bySource: originRows,
      byCurrency: currencyRows,
    },
    relationships: {
      topSuppliers: stats?.top_suppliers || [],
      email: {
        processed: toNumber(emailStats.processed_count),
        avgDaily: toNumber(emailStats.avg_daily),
        senders: emailStats.top_senders || [],
        cloud: emailStats.subject_word_cloud || [],
        latest: emailStats.recent_files || [],
      },
    },
    extractionFallback: {
      triggered: toNumber(fallbackStats?.triggered_count),
      multimodal: toNumber(fallbackStats?.multimodal_count),
      textract: toNumber(fallbackStats?.textract_count),
      inputTokens: toNumber(fallbackStats?.total_input_tokens),
      outputTokens: toNumber(fallbackStats?.total_output_tokens),
      costUsd: toNumber(fallbackStats?.total_cost_usd),
    },
    isOnboarding: toNumber(stats?.total_documents) < ONBOARDING_THRESHOLD,
  };
}

const formatValue = (value, format) => {
  if (format === 'percent') return `${(toNumber(value) * 100).toFixed(1)} %`;
  if (format === 'duration') return `${toNumber(value).toFixed(1)} s`;
  if (format === 'mb') return formatMb(toNumber(value));
  return formatCount(Math.round(toNumber(value)));
};

const CountUp = ({ value, format }) => {
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setDisplay(value);
      return undefined;
    }

    let raf;
    const from = 0;
    const start = performance.now();
    const duration = 800;

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (toNumber(value) - from) * eased);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return <>{formatValue(display, format)}</>;
};

const Sparkline = ({ data = [], color = '#0066cc', width = 130, height = 44 }) => {
  const points = data.length ? data : [0, 0, 0, 0, 0, 0, 0];
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const span = max - min || 1;
  const step = width / Math.max(points.length - 1, 1);
  const coords = points.map((value, index) => [
    index * step,
    height - 4 - ((value - min) / span) * (height - 8),
  ]);
  const line = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

  return (
    <svg className="command-sparkline" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline points={line} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const Delta = ({ value = 0, lowerIsBetter = false }) => {
  const delta = toNumber(value);
  if (Math.abs(delta) < 0.05) {
    return <span className="command-delta command-delta--flat">sin cambios</span>;
  }
  const good = lowerIsBetter ? delta < 0 : delta > 0;
  return (
    <span className={`command-delta ${good ? 'command-delta--up' : 'command-delta--down'}`}>
      {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
      <span>vs período ant.</span>
    </span>
  );
};

const SectionTitle = ({ children }) => (
  <div className="command-section-title">
    <span>{children}</span>
  </div>
);

const RangeSelector = ({ range, onChange }) => (
  <div className="command-seg" aria-label="Rango del dashboard">
    {RANGES.map(([value, label]) => (
      <button
        type="button"
        key={value}
        className={value === range ? 'active' : ''}
        onClick={() => onChange(value)}
      >
        {label}
      </button>
    ))}
  </div>
);

const HealthStrip = ({ health, lastUpdated }) => {
  const meta = STATUS_META[health.status] || STATUS_META.ok;
  return (
    <div className="command-health">
      <div className="command-health-inner">
        <span className="command-health-status">
          <span className={`command-dot ${meta.dot}`} />
          {meta.label}
        </span>
        <span className="command-hchip">Éxito <b>{pct(health.successRate)}</b></span>
        <span className="command-hchip">En proceso <b>{formatCount(health.inProcess)}</b></span>
        <span className={`command-hchip ${health.openErrors > 0 ? 'alert' : ''}`}>
          Errores <b>{formatCount(health.openErrors)}</b>
        </span>
        <span className="command-hchip">⏱ <b>{health.avgSeconds.toFixed(1)}s</b>/archivo</span>
        <span className="command-updated">
          Actualizado {formatRelativeTime(lastUpdated)}
        </span>
      </div>
    </div>
  );
};

const AttentionCard = ({ items }) => {
  const actionable = items.filter((item) => item.count > 0);

  return (
    <article className="command-card command-card--pad command-card--lift command-attention">
      <h3>Requiere tu atención</h3>
      {actionable.length === 0 ? (
        <div className="command-attention-calm">
          <span className="command-check" aria-hidden="true">✓</span>
          <strong>Todo al día</strong>
          <span>No hay comprobantes pendientes ni errores que resolver.</span>
        </div>
      ) : (
        actionable.map((item) => (
          <Link
            key={item.key}
            className={`command-attention-item ${item.severity}`}
            to={item.href}
          >
            <span className="left">
              <strong>{formatCount(item.count)}</strong>
              <span>{item.label}</span>
            </span>
            <span className="arr">→</span>
          </Link>
        ))
      )}
    </article>
  );
};

const KpiCard = ({ kpi }) => (
  <article className="command-card command-card--pad command-card--lift command-kpi">
    <span className="command-eyebrow">{kpi.label}</span>
    <strong className="command-kpi-value">
      <CountUp value={kpi.value} format={kpi.format} />
    </strong>
    <div className="command-kpi-foot">
      <Delta value={kpi.deltaPct} lowerIsBetter={kpi.lowerIsBetter} />
      <Sparkline data={kpi.trend} color={kpi.lowerIsBetter ? '#059669' : '#0066cc'} width={92} height={28} />
    </div>
  </article>
);

const CompactList = ({ title, rows }) => (
  <article className="command-card command-card--pad command-card--lift command-comp">
    <h4>{title}</h4>
    {rows.length === 0 ? (
      <p className="command-empty">Sin datos disponibles.</p>
    ) : (
      rows.map((row) => (
        <div key={`${title}-${row.key || row.label}`} className="command-crow-wrap">
          <div className="command-crow">
            <span className="swatch" style={{ background: row.color || '#0066cc' }} />
            <span className="clabel">{row.label}</span>
            <span className="cval">{formatCount(row.count)} · {pct(row.pct)}</span>
          </div>
          <div className="command-cbar">
            <span style={{ width: `${Math.max(row.pct * 100, row.count > 0 ? 4 : 0)}%`, background: row.color || '#0066cc' }} />
          </div>
        </div>
      ))
    )}
  </article>
);

const CompositionSection = ({ composition }) => (
  <div className="command-grid command-comp-grid">
    <CompactList title="Por subtipo" rows={composition.bySubtype} />
    <CompactList title="Origen de ingreso" rows={composition.bySource} />
    <CompactList title="Moneda" rows={composition.byCurrency} />
  </div>
);

const SuppliersCard = ({ suppliers }) => (
  <article className="command-card command-card--pad command-card--lift command-rel">
    <h4>Top proveedores</h4>
    <p className="sub">Por cantidad de comprobantes</p>
    {suppliers.length === 0 ? (
      <p className="command-empty">Sin comprobantes con emisor identificado.</p>
    ) : (
      suppliers.slice(0, 5).map((supplier, index) => (
        <Link
          key={`${supplier.cuit || supplier.name}-${index}`}
          className="command-supplier"
          to={buildFileManagerLink(supplier.cuit ? { supplier_cuit: supplier.cuit } : {})}
        >
          <span className="rank">{supplier.rank || index + 1}</span>
          <span className="supplier-main">
            <strong title={supplier.name}>{supplier.name || 'Proveedor sin nombre'}</strong>
            <span>{supplier.cuit || 'CUIT no disponible'}</span>
          </span>
          <span>{formatCount(supplier.document_count)} comp.</span>
        </Link>
      ))
    )}
  </article>
);

const EmailCard = ({ email }) => {
  const maxWeight = Math.max(...email.cloud.map((item) => toNumber(item.weight)), 1);
  return (
    <article className="command-card command-card--pad command-card--lift command-rel">
      <h4>Actividad por email</h4>
      <p className="sub">Captura de comprobantes vía correo</p>
      <div className="command-email-stats">
        <div><strong>{formatCount(email.processed)}</strong><span>Capturados</span></div>
        <div><strong>{email.avgDaily.toFixed(1)}</strong><span>Prom. diario</span></div>
        <div><strong>{formatCount(email.senders.length)}</strong><span>Remitentes</span></div>
      </div>
      <div className="command-cloud">
        {email.cloud.length === 0 ? (
          <span className="command-empty">Sin asuntos frecuentes.</span>
        ) : email.cloud.slice(0, 8).map((item) => {
          const weight = toNumber(item.weight);
          return (
            <span
              key={item.text}
              style={{
                fontSize: `${13 + (weight / maxWeight) * 14}px`,
                opacity: 0.55 + (weight / maxWeight) * 0.45,
              }}
            >
              {item.text}
            </span>
          );
        })}
      </div>
      <div className="command-latest-title">Últimos recibidos</div>
      {email.latest.length === 0 ? (
        <p className="command-empty">Sin emails procesados recientemente.</p>
      ) : email.latest.slice(0, 4).map((file) => (
        <Link
          key={`${file.document_id}-${file.email_id || file.filename}`}
          className="command-latest"
          to={file.document_id ? `/documents/${file.document_id}` : buildFileManagerLink({ channel: 'email' })}
        >
          <strong>{file.filename}</strong>
          <span>{file.email_subject || file.email_from || 'Email recibido'} · {formatRelativeTime(file.end_at)}</span>
        </Link>
      ))}
    </article>
  );
};

const FallbackUsageCard = ({ fallback, rangeLabel }) => {
  if (!fallback?.triggered) return null;
  const totalTokens = fallback.inputTokens + fallback.outputTokens;
  return (
    <article className="command-card command-card--pad command-card--lift command-fallback">
      <h4>Fallback extracción emisor</h4>
      <p className="sub">
        PDFs híbridos (template + overlays) · {rangeLabel}
      </p>
      <div className="command-fallback-stats">
        <div>
          <strong>{formatCount(fallback.triggered)}</strong>
          <span>Usos fallback</span>
        </div>
        <div>
          <strong>{formatCount(fallback.multimodal)}</strong>
          <span>Multimodal</span>
        </div>
        <div>
          <strong>{formatCount(fallback.textract)}</strong>
          <span>Textract</span>
        </div>
        <div>
          <strong>{formatCount(totalTokens)}</strong>
          <span>Tokens</span>
        </div>
        <div>
          <strong>${fallback.costUsd.toFixed(4)}</strong>
          <span>Costo extra USD</span>
        </div>
      </div>
    </article>
  );
};

const RelationshipsSection = ({ relationships, extractionFallback, rangeLabel }) => (
  <div className="command-grid command-rel-grid">
    <SuppliersCard suppliers={relationships.topSuppliers} />
    <EmailCard email={relationships.email} />
    <FallbackUsageCard fallback={extractionFallback} rangeLabel={rangeLabel} />
  </div>
);

const OnboardingBanner = ({ processed }) => (
  <article className="command-card command-card--pad command-onboarding">
    <div>
      <h3>Estás dando tus primeros pasos</h3>
      <p>
        Llevás {formatCount(processed)} comprobantes procesados. Las tendencias y la composición
        se enriquecen a partir de ~20 documentos.
      </p>
    </div>
    <Link className="command-primary-btn" to="/upload">Procesar un lote</Link>
  </article>
);

const DashboardPulse = ({ stats, lastUpdated, loading, onRefresh }) => {
  const [range, setRange] = useState('7d');
  const data = useMemo(() => buildCommandCenterModel(stats, range), [stats, range]);
  if (!stats) return null;

  return (
    <section className="command-center" aria-label="Dashboard Command Center">
      <div className="command-controls">
        <RangeSelector range={range} onChange={setRange} />
        <button
          type="button"
          className={`command-refresh ${loading ? 'spin' : ''}`}
          onClick={onRefresh}
          disabled={loading}
        >
          ↻ Actualizar
        </button>
      </div>

      <HealthStrip health={data.health} lastUpdated={lastUpdated || stats.updated_at} />

      <div className="command-main">
        {data.isOnboarding && <OnboardingBanner processed={data.hero.processed} />}

        <div className="command-hero-row">
          <article className="command-card command-card--pad command-card--lift command-hero">
            <span className="command-eyebrow">Procesados · {data.rangeLabel}</span>
            <div className="command-hero-big-wrap">
              <strong className="command-hero-big">
                <CountUp value={data.hero.processed} format="integer" />
              </strong>
              <Sparkline data={data.hero.trend} color="#0066cc" width={150} height={48} />
            </div>
            <p>
              <b>{formatMb(data.hero.volumeMb)}</b> en archivos · <Delta value={data.hero.deltaPct} />
            </p>
          </article>
          <AttentionCard items={data.attention} />
        </div>

        <div className="command-grid command-kpi-grid">
          {data.kpis.map((kpi) => <KpiCard key={kpi.key} kpi={kpi} />)}
        </div>

        <SectionTitle>Composición</SectionTitle>
        <CompositionSection composition={data.composition} />

        <SectionTitle>Relaciones</SectionTitle>
        <RelationshipsSection
          relationships={data.relationships}
          extractionFallback={data.extractionFallback}
          rangeLabel={data.rangeLabel}
        />
      </div>
    </section>
  );
};

export default DashboardPulse;
